import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Check, X, Loader2 } from "lucide-react";

function TextInput({ label, name, value, onChange, placeholder }) {
  return (
    <label className="block">
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <input
        name={name} value={value || ""} onChange={onChange} placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"
      />
    </label>
  );
}

/* -------- Verifiers -------- */
export function VerifiersAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ role: "title-guardian", name: "", firm: "", country: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => setItems((await api.get("/verifiers")).data.verifiers || []);
  useEffect(() => { load(); }, []);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const add = async () => {
    if (!form.name || !form.country) return toast.error("Name and country are required.");
    setBusy(true);
    try { await api.post("/admin/verifiers", form); toast.success("Added."); setForm({ role: "title-guardian", name: "", firm: "", country: "", email: "", phone: "", notes: "" }); await load(); }
    catch { toast.error("Could not add."); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/verifiers/${id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="admin-verifiers">
      <div className="mv-card p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Role</span>
          <select name="role" value={form.role} onChange={update} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]">
            <option value="title-guardian">Title Guardian</option>
            <option value="review-panel">Review Panel</option>
          </select>
        </label>
        <TextInput label="Name" name="name" value={form.name} onChange={update}/>
        <TextInput label="Firm" name="firm" value={form.firm} onChange={update}/>
        <TextInput label="Country" name="country" value={form.country} onChange={update}/>
        <TextInput label="Email" name="email" value={form.email} onChange={update}/>
        <TextInput label="Phone" name="phone" value={form.phone} onChange={update}/>
        <div className="md:col-span-3">
          <TextInput label="Notes" name="notes" value={form.notes} onChange={update}/>
        </div>
        <button onClick={add} disabled={busy} className="btn-green md:col-span-3">{busy ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>} Add verifier</button>
      </div>
      <ul className="mv-card divide-y mv-hairline">
        {items.map((v) => (
          <li key={v.id} className="p-4 flex justify-between items-center gap-3">
            <div>
              <div className="font-semibold mv-green-text">{v.name} <span className="text-[11px] px-2 py-0.5 rounded-full ml-1" style={{ background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>{v.role}</span></div>
              <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>{v.firm} · {v.country}</div>
            </div>
            <button onClick={() => remove(v.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl"><Trash2 size={16}/></button>
          </li>
        ))}
        {!items.length && <li className="p-6 text-neutral-500 text-[13.5px]">No verifiers yet.</li>}
      </ul>
    </div>
  );
}

/* -------- Builders -------- */
export function BuildersAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", company: "", country: "", city: "", specialties: "", fair_wage_verified: true, contact: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => setItems((await api.get("/builders")).data.builders || []);
  useEffect(() => { load(); }, []);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const add = async () => {
    if (!form.name || !form.country) return toast.error("Name and country are required.");
    setBusy(true);
    try { await api.post("/admin/builders", form); toast.success("Added."); setForm({ name: "", company: "", country: "", city: "", specialties: "", fair_wage_verified: true, contact: "", notes: "" }); await load(); }
    catch { toast.error("Could not add."); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/builders/${id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="admin-builders">
      <div className="mv-card p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <TextInput label="Name" name="name" value={form.name} onChange={update}/>
        <TextInput label="Company" name="company" value={form.company} onChange={update}/>
        <TextInput label="Country" name="country" value={form.country} onChange={update}/>
        <TextInput label="City" name="city" value={form.city} onChange={update}/>
        <TextInput label="Contact" name="contact" value={form.contact} onChange={update}/>
        <label className="block">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Fair-wage verified</span>
          <select value={form.fair_wage_verified ? "yes" : "no"} onChange={(e) => setForm({ ...form, fair_wage_verified: e.target.value === "yes" })} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]">
            <option value="yes">Yes</option><option value="no">No</option>
          </select>
        </label>
        <div className="md:col-span-3">
          <TextInput label="Specialties" name="specialties" value={form.specialties} onChange={update} placeholder="e.g. Family homes, Solar, Ecodesign"/>
        </div>
        <button onClick={add} disabled={busy} className="btn-green md:col-span-3">{busy ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>} Add builder</button>
      </div>
      <ul className="mv-card divide-y mv-hairline">
        {items.map((b) => (
          <li key={b.id} className="p-4 flex justify-between items-center gap-3">
            <div>
              <div className="font-semibold mv-green-text">{b.name}{b.fair_wage_verified && <span className="text-[11px] px-2 py-0.5 rounded-full ml-2" style={{ background: "rgba(37,211,102,0.14)", color: "#0A7A2C" }}>Fair-wage</span>}</div>
              <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>{b.company} · {b.city}{b.city ? ", " : ""}{b.country}</div>
            </div>
            <button onClick={() => remove(b.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl"><Trash2 size={16}/></button>
          </li>
        ))}
        {!items.length && <li className="p-6 text-neutral-500 text-[13.5px]">No builders yet.</li>}
      </ul>
    </div>
  );
}

/* -------- Hardship review -------- */
export function HardshipAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/admin/hardship-requests")).data.requests || []);
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    const note = prompt(`Optional note for ${decision}:`) || "";
    await api.post(`/admin/hardship-requests/${id}/decision`, { decision, note });
    await load();
  };
  return (
    <div className="mv-card divide-y mv-hairline" data-testid="admin-hardship">
      {items.map((r) => (
        <div key={r.id} className="p-4 flex flex-wrap justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold mv-green-text">{r.user_name || r.user_email}</div>
            <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>{r.months_requested} month · {(r.created_at || "").slice(0,10)}</div>
            <div className="text-[13.5px] mt-1">{r.reason}</div>
            {r.admin_note && <div className="text-[12.5px] italic mt-1">Note: {r.admin_note}</div>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] px-2 py-1 rounded-full mr-2" style={{ background: "rgba(0,0,0,0.06)" }}>{r.status}</span>
            {r.status === "pending" && (
              <>
                <button onClick={() => decide(r.id, "approved")} className="btn-green !py-1.5 !px-3"><Check size={14}/> Approve</button>
                <button onClick={() => decide(r.id, "rejected")} className="btn-ghost !py-1.5 !px-3"><X size={14}/> Reject</button>
              </>
            )}
          </div>
        </div>
      ))}
      {!items.length && <div className="p-6 text-neutral-500 text-[13.5px]">No hardship requests.</div>}
    </div>
  );
}

/* -------- Stories -------- */
export function StoriesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ author_alias: "", country: "", year: new Date().getFullYear(), story: "", title_ref_hash: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => setItems((await api.get("/stories")).data.stories || []);
  useEffect(() => { load(); }, []);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const add = async () => {
    if (!form.author_alias || !form.story || !form.country) return toast.error("Author alias, country and story are required.");
    setBusy(true);
    try { await api.post("/admin/stories", { ...form, year: parseInt(form.year) || new Date().getFullYear() }); toast.success("Published."); setForm({ author_alias: "", country: "", year: new Date().getFullYear(), story: "", title_ref_hash: "" }); await load(); }
    catch { toast.error("Could not publish."); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/stories/${id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="admin-stories">
      <div className="mv-card p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextInput label="Author alias" name="author_alias" value={form.author_alias} onChange={update}/>
        <TextInput label="Country" name="country" value={form.country} onChange={update}/>
        <TextInput label="Year" name="year" value={form.year} onChange={update}/>
        <TextInput label="Anonymised title ref" name="title_ref_hash" value={form.title_ref_hash} onChange={update} placeholder="e.g. ZW-2026-HRE-***91"/>
        <label className="block md:col-span-2">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Story</span>
          <textarea name="story" value={form.story} onChange={update} rows={4} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
        </label>
        <button onClick={add} disabled={busy} className="btn-green md:col-span-2">{busy ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>} Publish verified story</button>
      </div>
      <ul className="mv-card divide-y mv-hairline">
        {items.map((s) => (
          <li key={s.id} className="p-4 flex justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold mv-green-text">{s.author_alias} <span className="text-[12px] font-normal" style={{ color: "var(--mv-muted)" }}>· {s.country} · {s.year}</span></div>
              <div className="text-[13.5px] mt-1 line-clamp-2">{s.story}</div>
              {s.title_ref_hash && <div className="text-[11px] mt-1" style={{ color: "var(--mv-muted)" }}>Ref: {s.title_ref_hash}</div>}
            </div>
            <button onClick={() => remove(s.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl h-fit"><Trash2 size={16}/></button>
          </li>
        ))}
        {!items.length && <li className="p-6 text-neutral-500 text-[13.5px]">No stories published yet.</li>}
      </ul>
    </div>
  );
}

/* -------- Roadmap -------- */
export function RoadmapAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "feature" });
  const [busy, setBusy] = useState(false);
  const load = async () => setItems((await api.get("/roadmap")).data.items || []);
  useEffect(() => { load(); }, []);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const add = async () => {
    if (!form.title || !form.description) return toast.error("Title & description required.");
    setBusy(true);
    try { await api.post("/admin/roadmap", form); toast.success("Added."); setForm({ title: "", description: "", category: "feature" }); await load(); }
    catch { toast.error("Could not add."); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/roadmap/${id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="admin-roadmap">
      <div className="mv-card p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        <TextInput label="Title" name="title" value={form.title} onChange={update}/>
        <TextInput label="Category" name="category" value={form.category} onChange={update} placeholder="feature | country | payments | product | social"/>
        <label className="block md:col-span-2">
          <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Description</span>
          <textarea name="description" value={form.description} onChange={update} rows={2} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
        </label>
        <button onClick={add} disabled={busy} className="btn-green md:col-span-2">{busy ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>} Add roadmap item</button>
      </div>
      <ul className="mv-card divide-y mv-hairline">
        {items.map((r) => (
          <li key={r.id} className="p-4 flex justify-between gap-3">
            <div>
              <div className="font-semibold mv-green-text">{r.title} <span className="text-[11px] px-2 py-0.5 rounded-full ml-1" style={{ background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>{r.category}</span></div>
              <div className="text-[13px] mt-1" style={{ color: "var(--mv-muted)" }}>{r.description}</div>
              <div className="text-[12px] mt-1"><b>{r.votes}</b> votes</div>
            </div>
            <button onClick={() => remove(r.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl h-fit"><Trash2 size={16}/></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------- Builder applications (review + approve/reject) -------- */
export function BuilderApplicationsAdmin() {
  const [items, setItems] = useState([]);
  const load = async () => setItems((await api.get("/admin/builder-applications")).data.applications || []);
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    if (!confirm("Approve this applicant and publish them on the Builders directory?")) return;
    try {
      await api.post(`/admin/builder-applications/${id}/approve`);
      toast.success("Approved and published.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve."); }
  };
  const reject = async (id) => {
    const note = prompt("Reason (optional):") || "";
    try { await api.post(`/admin/builder-applications/${id}/reject`, { note }); await load(); }
    catch { toast.error("Could not reject."); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this application?")) return;
    await api.delete(`/admin/builder-applications/${id}`); await load();
  };

  const badge = (status) => {
    const s = { pending:  { bg: "rgba(212,175,55,0.16)", color: "#8a6b12" },
                approved: { bg: "rgba(37,211,102,0.14)", color: "#0A7A2C" },
                rejected: { bg: "rgba(220,53,69,0.12)", color: "#a11d29" } }[status || "pending"];
    return <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>{status || "pending"}</span>;
  };

  return (
    <div className="mv-card divide-y mv-hairline" data-testid="admin-builder-applications">
      {items.map((a) => (
        <div key={a.id} className="p-5 flex flex-wrap gap-4 justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold mv-green-text">{a.contact_name}</div>
              {badge(a.status)}
              {a.company && <span className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>· {a.company}</span>}
            </div>
            <div className="text-[13px] mt-0.5" style={{ color: "var(--mv-muted)" }}>
              {a.city ? a.city + ", " : ""}{a.country} · {a.phone || a.email || "no contact"}
            </div>
            {a.specialties && <div className="text-[13px] mt-2"><b>Specialties:</b> {a.specialties}</div>}
            {a.references   && <div className="text-[13px] mt-1"><b>References:</b> {a.references}</div>}
            {a.sample_pricing && <div className="text-[13px] mt-1"><b>Pricing:</b> {a.sample_pricing}</div>}
            {a.notes && <div className="text-[13px] mt-1 italic">Notes: {a.notes}</div>}
            {a.admin_note && <div className="text-[12.5px] mt-2 italic" style={{ color: "#a11d29" }}>Admin note: {a.admin_note}</div>}
            <div className="text-[11px] mt-2" style={{ color: "var(--mv-muted)" }}>{(a.created_at || "").slice(0,10)}</div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {a.status !== "approved" && (
              <button onClick={() => approve(a.id)} className="btn-green !py-1.5 !px-3" data-testid={`app-approve-${a.id}`}>
                <Check size={14}/> Approve & Publish
              </button>
            )}
            {a.status === "pending" && (
              <button onClick={() => reject(a.id)} className="btn-ghost !py-1.5 !px-3" data-testid={`app-reject-${a.id}`}>
                <X size={14}/> Reject
              </button>
            )}
            <button onClick={() => remove(a.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl self-end"><Trash2 size={16}/></button>
          </div>
        </div>
      ))}
      {!items.length && <div className="p-6 text-neutral-500 text-[13.5px]">No applications yet. Share the WhatsApp draft to receive some.</div>}
    </div>
  );
}

export function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | project.id
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/projects");
    setItems(Array.isArray(data) ? data : (data.projects || []));
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({
      slug: "", name: "", city: "", country: "", country_flag: "", type: "Residential",
      status: "Selling Now", units_total: 0, units_available: 0,
      price_from: 0, currency: "GBP", bedrooms: "", handover: "",
      hero_image: "", description: "", address: "", lat: 0, lng: 0,
      featured: false, local_reservation_pct: 40,
    });
  };
  const startEdit = (p) => { setEditing(p.id); setForm({ ...p }); };
  const cancel = () => { setEditing(null); setForm(null); };
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        price_from: parseFloat(form.price_from) || 0,
        lat: form.lat === "" ? null : parseFloat(form.lat),
        lng: form.lng === "" ? null : parseFloat(form.lng),
        local_reservation_pct: parseFloat(form.local_reservation_pct) || 0,
        units_total: parseInt(form.units_total) || 0,
        units_available: parseInt(form.units_available) || 0,
      };
      if (editing === "new") {
        if (!form.slug || !form.name || !form.country) return toast.error("Slug, name, country required.");
        await api.post("/admin/projects", payload);
        toast.success("Project created.");
      } else {
        await api.patch(`/admin/projects/${editing}`, payload);
        toast.success("Project updated.");
      }
      cancel(); await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  };

  const remove = async (pid) => {
    if (!confirm("Delete this project?")) return;
    await api.delete(`/admin/projects/${pid}`); await load();
  };

  return (
    <div className="space-y-4" data-testid="admin-projects">
      {editing ? (
        <div className="mv-card p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <TextInput label="Slug (unique id)" value={form.slug} onChange={(e) => set("slug", e.target.value)}/>
          <TextInput label="Name" value={form.name} onChange={(e) => set("name", e.target.value)}/>
          <TextInput label="Country" value={form.country} onChange={(e) => set("country", e.target.value)}/>
          <TextInput label="City" value={form.city} onChange={(e) => set("city", e.target.value)}/>
          <TextInput label="Country flag emoji" value={form.country_flag} onChange={(e) => set("country_flag", e.target.value)}/>
          <TextInput label="Type" value={form.type} onChange={(e) => set("type", e.target.value)} placeholder="Residential / Villas / Apartments"/>
          <label className="block">
            <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Status</span>
            <select value={form.status} onChange={(e) => set("status", e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]">
              <option>Selling Now</option>
              <option>Off-Plan · Reserve Now</option>
              <option>Coming Soon</option>
            </select>
          </label>
          <TextInput label="Price from" value={form.price_from} onChange={(e) => set("price_from", e.target.value)}/>
          <TextInput label="Currency" value={form.currency} onChange={(e) => set("currency", e.target.value)}/>
          <TextInput label="Units total" value={form.units_total} onChange={(e) => set("units_total", e.target.value)}/>
          <TextInput label="Units available" value={form.units_available} onChange={(e) => set("units_available", e.target.value)}/>
          <TextInput label="Bedrooms" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} placeholder="e.g. 2-4"/>
          <TextInput label="Handover" value={form.handover} onChange={(e) => set("handover", e.target.value)} placeholder="e.g. Dec 2026"/>
          <TextInput label="Local reservation %" value={form.local_reservation_pct} onChange={(e) => set("local_reservation_pct", e.target.value)}/>
          <TextInput label="Latitude" value={form.lat} onChange={(e) => set("lat", e.target.value)}/>
          <TextInput label="Longitude" value={form.lng} onChange={(e) => set("lng", e.target.value)}/>
          <TextInput label="Hero image URL" value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)}/>
          <TextInput label="Address" value={form.address} onChange={(e) => set("address", e.target.value)}/>
          <label className="block">
            <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Featured</span>
            <select value={form.featured ? "yes" : "no"} onChange={(e) => set("featured", e.target.value === "yes")}
              className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]">
              <option value="no">No</option><option value="yes">Yes</option>
            </select>
          </label>
          <label className="block md:col-span-3">
            <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Description</span>
            <textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3}
              className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
          </label>
          <div className="md:col-span-3 flex gap-3">
            <button onClick={save} disabled={busy} className="btn-green" data-testid="project-save">
              {busy ? <Loader2 className="animate-spin" size={16}/> : <Check size={16}/>}
              {editing === "new" ? "Create project" : "Save changes"}
            </button>
            <button onClick={cancel} className="btn-ghost">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startNew} className="btn-green" data-testid="project-new"><Plus size={16}/> New project</button>
      )}
      <ul className="mv-card divide-y mv-hairline">
        {items.map((p) => (
          <li key={p.id} className="p-4 flex flex-wrap justify-between gap-3 items-center">
            <div>
              <div className="font-semibold mv-green-text">{p.name} <span className="text-[11px] px-2 py-0.5 rounded-full ml-2" style={{ background: "rgba(212,175,55,0.16)", color: "#8a6b12" }}>{p.status}</span></div>
              <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>{p.city ? p.city+", " : ""}{p.country} · {p.currency} {Number(p.price_from).toLocaleString()}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(p)} className="btn-ghost !py-1.5 !px-3" data-testid={`project-edit-${p.id}`}>Edit</button>
              <button onClick={() => remove(p.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl"><Trash2 size={16}/></button>
            </div>
          </li>
        ))}
        {!items.length && <li className="p-6 text-neutral-500 text-[13.5px]">No projects yet.</li>}
      </ul>
    </div>
  );
}

export function ImpactAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ month: new Date().toISOString().slice(0,7), homes_delivered: 0, families_helped: 0, fees_saved: 0, currency: "GBP", summary: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => setItems((await api.get("/impact-reports")).data.reports || []);
  useEffect(() => { load(); }, []);
  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const add = async () => {
    if (!form.month) return toast.error("Month is required.");
    setBusy(true);
    try {
      await api.post("/admin/impact-reports", {
        ...form,
        homes_delivered: parseInt(form.homes_delivered) || 0,
        families_helped: parseInt(form.families_helped) || 0,
        fees_saved: parseFloat(form.fees_saved) || 0,
      });
      toast.success("Report published."); setForm({ month: new Date().toISOString().slice(0,7), homes_delivered: 0, families_helped: 0, fees_saved: 0, currency: "GBP", summary: "" });
      await load();
    } catch { toast.error("Could not publish."); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/admin/impact-reports/${id}`); await load(); };
  return (
    <div className="space-y-4" data-testid="admin-impact">
      <div className="mv-card p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
        <TextInput label="Month (YYYY-MM)" name="month" value={form.month} onChange={update}/>
        <TextInput label="Homes delivered" name="homes_delivered" value={form.homes_delivered} onChange={update}/>
        <TextInput label="Families helped" name="families_helped" value={form.families_helped} onChange={update}/>
        <TextInput label="Fees saved" name="fees_saved" value={form.fees_saved} onChange={update}/>
        <TextInput label="Currency" name="currency" value={form.currency} onChange={update}/>
        <div className="md:col-span-3">
          <label className="block">
            <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Summary</span>
            <textarea name="summary" value={form.summary} onChange={update} rows={2} className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[13.5px]"/>
          </label>
        </div>
        <button onClick={add} disabled={busy} className="btn-green md:col-span-3">{busy ? <Loader2 className="animate-spin" size={16}/> : <Plus size={16}/>} Publish report</button>
      </div>
      <ul className="mv-card divide-y mv-hairline">
        {items.map((r) => (
          <li key={r.id} className="p-4 flex justify-between gap-3">
            <div>
              <div className="font-semibold mv-green-text">{r.month}</div>
              <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>{r.homes_delivered} homes · {r.families_helped} families · {r.currency} {Number(r.fees_saved).toLocaleString()}</div>
              {r.summary && <div className="text-[13px] mt-1">{r.summary}</div>}
            </div>
            <button onClick={() => remove(r.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-xl h-fit"><Trash2 size={16}/></button>
          </li>
        ))}
        {!items.length && <li className="p-6 text-neutral-500 text-[13.5px]">No reports yet.</li>}
      </ul>
    </div>
  );
}
